// Resizable, remembered column widths — the factory grid's drag, double-click
// and reset (sites/factory/js/grid.js), for any <table> whose header row ends
// in a .filler cell, as every dashboard table's does. The table's markup stays
// whatever wrote it: this adds a grip to each header, sizes the headers, and
// remembers the result under 'colw:<table key>' in localStorage, per browser,
// exactly as the factory remembers its grids.
//
// The rules are the factory's, restated beside the code they govern:
//
// FREEZE ONLY WHEN EVERY VISIBLE COLUMN HAS A REMEMBERED WIDTH. A fixed layout
// hands each column exactly its `width` and splits what is left equally
// between the ones that have none — and a drag used to save the ONE column
// that was dragged. So resizing one column froze the whole table for good:
// on the next load the other twenty shared the leftovers at ~44px each and the
// grid read as nothing but ellipses, with no sideways scroll to escape into.
// With a partial set the table stays in AUTO layout, where every column sizes
// itself to its own content and the filler takes the rest; the remembered
// widths lie dormant until the set is whole again (a column added later has
// no saved width by definition). A drag therefore pins and remembers EVERY
// column, so the set it leaves is always whole.
//
// WHOLE PIXELS FROM THE START. A column sized by its own text lands on a
// fraction — 125.14px — and a fraction cannot be written down: rounding it
// down cuts the header (text-overflow takes off as many characters as the
// "…" needs), rounding it up moves the column. So every render gives each
// column its measured width, rounded up, as a floor (min-width, which is what
// the auto layout obeys), and everything afterwards measures a whole number
// and writes back the same whole number.

// The narrowest a column may be dragged or fitted to. Below this a header
// label is gone and the column says nothing at all.
export const MIN_COL = 48;

export class TableColumns {
  // table(): finds the live table on demand — a card parks it while the list
  // is empty and brings the same nodes back, so a reference would go stale.
  // onChange: called after widths are saved or forgotten, so the owner can
  // show or hide its reset button.
  constructor({ table, storageKey, onChange }) {
    this.table = table;
    this.storageKey = storageKey;
    this.onChange = onChange;
    this.widths = this.load();
  }

  load() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      return saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};
    } catch (e) {
      return {};
    }
  }

  save() {
    try {
      if (Object.keys(this.widths).length) localStorage.setItem(this.storageKey, JSON.stringify(this.widths));
      else localStorage.removeItem(this.storageKey); // nothing left to remember
    } catch (e) { /* full or blocked: the widths last the page */ }
    this.onChange?.();
  }

  hasWidths() {
    return Object.keys(this.widths).length > 0;
  }

  // The user's columns: every header but the filler, which exists to give
  // its width away and is not anyone's to size.
  heads(table) {
    return [...table.querySelectorAll('thead th:not(.filler)')];
  }

  // What a column is remembered under: its data-col if the markup names one,
  // else its own title, else its place in the row (the action column has no
  // title). Set once per header element, so it holds still for the element's
  // life whatever appears or disappears beside it.
  key(th, index) {
    const title = (th.dataset.col || th.textContent || '').trim().toLowerCase().replace(/\s+/g, '_');
    return title || '_' + index;
  }

  // After every render: a grip on every header (once), then the freeze-or-thaw
  // decision and the whole-pixel floors. Cheap and idempotent, so the owner
  // can call it whenever anything under the table changed.
  sync() {
    const table = this.table();
    if (!table) return;
    const ths = this.heads(table);
    ths.forEach((th, index) => {
      if (!th.dataset.colkey) th.dataset.colkey = this.key(th, index);
      if (!th.querySelector(':scope > .col-resizer')) th.append(this.grip(th));
    });
    this.apply(table, ths);
  }

  apply(table, ths) {
    const whole = ths.length > 0 && ths.every((th) => this.widths[th.dataset.colkey] > 0);
    if (whole) {
      for (const th of ths) {
        th.style.width = this.widths[th.dataset.colkey] + 'px';
        th.style.minWidth = '0';
      }
      table.classList.add('frozen');
      return;
    }
    table.classList.remove('frozen');
    for (const th of ths) th.style.width = '';
    this.roundColumns(ths);
  }

  roundColumns(ths) {
    // Let go of last render's floors first, or a column could only ever grow:
    // the floor would be measured as this render's own width. Then read every
    // column before writing any — reading after each write would lay the whole
    // table out once per column.
    for (const th of ths) th.style.minWidth = '';
    const want = ths.map((th) => this.shownWidth(th));
    ths.forEach((th, i) => { th.style.minWidth = want[i] + 'px'; });
  }

  // The width a column is REALLY showing, rounded UP to the next whole pixel.
  // Never offsetWidth: it is an integer and rounds DOWN as often as up, and a
  // fraction of a pixel is enough for text-overflow to cut a header.
  shownWidth(el) {
    return Math.ceil(parseFloat(getComputedStyle(el).width)) || el.offsetWidth;
  }

  // The cap a column may fit itself to, read from the stylesheet (--col-max on
  // the table) so this file and web.css cannot disagree about it.
  colMax(table) {
    const v = parseFloat(getComputedStyle(table).getPropertyValue('--col-max'));
    return v > 0 ? v : 340;
  }

  // freezeColumns pins every column at the width it is showing right now and
  // remembers them all, not only the one being worked on: whoever drags a
  // column has already settled where the others sit.
  freezeColumns() {
    const table = this.table();
    if (!table) return;
    for (const th of this.heads(table)) {
      const w = this.shownWidth(th);
      th.style.width = w + 'px';
      th.style.minWidth = '0';
      this.widths[th.dataset.colkey] = w;
    }
    table.classList.add('frozen');
  }

  // The drag handle on a header's right edge — drag it to set the width,
  // double-click it to fit the column to its own content.
  grip(th) {
    const grip = document.createElement('span');
    grip.className = 'col-resizer';
    grip.title = 'Drag to resize, double-click to fit the content';
    grip.addEventListener('click', (e) => e.stopPropagation()); // never a sort
    grip.addEventListener('dblclick', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.autoFit(th);
    });
    grip.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      grip.classList.add('active');
      const startX = e.clientX;
      const startW = this.shownWidth(th);
      // clientX is viewport pixels; widths are element-space CSS pixels, which
      // a root zoom renders zoom× larger. Divide the pointer delta by the zoom
      // so the edge tracks the mouse 1:1.
      const zoom = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
      let dragging = false;
      const move = (ev) => {
        const dx = (ev.clientX - startX) / zoom;
        // NOTHING IS PINNED UNTIL THE POINTER ACTUALLY MOVES: pressing the
        // divider must not freeze and remember every column at once, or a
        // mis-click locks the table into widths nobody chose, and the
        // double-click could never tell a table still sizing itself from one
        // that had been dragged.
        if (!dragging) {
          if (Math.abs(dx) < 1) return;
          dragging = true;
          this.freezeColumns(); // pin every column where it stands, so only the dragged one moves
        }
        const w = Math.max(MIN_COL, startW + dx);
        th.style.width = w + 'px';
        this.widths[th.dataset.colkey] = w;
      };
      const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
        grip.classList.remove('active');
        if (dragging) this.save();
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    });
    return grip;
  }

  // autoFit sizes ONE column to what is in it — the double-click on the
  // divider, as spreadsheets have always answered it — capped at --col-max,
  // the same ceiling a freshly loaded table uses. A frozen table is the only
  // place a column can be out of shape: anywhere else every column already
  // shows exactly what it needs, and writing a width would pin the whole table
  // to whatever this page of rows happens to be.
  autoFit(th) {
    const table = this.table();
    if (!table || !table.classList.contains('frozen')) return;
    // Let go of this one column, read what it asks for, put everything back.
    // Cells never wrap, so a column's content width is exact.
    table.classList.remove('frozen');
    th.style.width = '';
    th.style.minWidth = '';
    const fit = Math.min(Math.max(MIN_COL, this.shownWidth(th)), this.colMax(table));
    table.classList.add('frozen');
    th.style.minWidth = '0';
    th.style.width = fit + 'px';
    this.widths[th.dataset.colkey] = fit;
    this.save();
  }

  // reset puts the columns back to sizing themselves from their content.
  // Dragging is remembered forever and per browser, so without this the only
  // way out of a layout someone no longer wants is to clear site data — and a
  // width dragged narrow on a laptop stays narrow on a wide monitor.
  reset() {
    this.widths = {};
    this.save();
    this.sync(); // with nothing left to obey, this thaws the table
  }
}
